import { useEffect, useState } from 'react';
import { api, fmt, fmtDate, fmtTimestamp } from '../api.ts';

type Status = 'PENDING' | 'IN_PROGRESS' | 'ON_HOLD' | 'COMPLETED' | 'SKIPPED';

export interface UndoPreview {
  allowed: boolean;
  blocked: string | null;
  kind: 'STAGE' | 'ENTRY' | 'RELEASE' | 'DELIVERED' | 'STOCK';
  heading?: string;
  subtitle?: string;
  lastWhat?: string | null;
  counts?: { name: string; now: number; after: number }[];
  changes?: string[];
  dispatchId?: number;
  undoUrl?: string;
  jobId: number;
  jobNumber: string;
  customer: string;
  box: string;
  jobStageId: number;
  entryId: number | null;
  stageName: string;
  from: Status;
  to: 'PENDING' | 'IN_PROGRESS' | 'REMOVED' | null;
  tookBack: { name: string; from: Status }[];
  removedQuantity: number;
  keptQuantity: number;
  boards: { name: string; sheets: number }[];
  currentStage: string | null;
  doneAt: string | null;
  doneBy: string | null;
  summary: string;
}

const DID: Partial<Record<Status, string>> = {
  COMPLETED: 'Finished', SKIPPED: 'Skipped', IN_PROGRESS: 'Started', ON_HOLD: 'Put on hold',
};

function when(s: string | null) {
  if (!s) return null;
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? fmtDate(s) : fmtTimestamp(s);
}

function done(p: UndoPreview) {
  const verb = p.kind === 'STOCK' ? p.lastWhat
    : p.kind === 'DELIVERED' ? 'Delivered'
    : p.kind === 'RELEASE' ? 'Released'
    : p.kind === 'ENTRY' ? 'Recorded'
    : p.to === 'REMOVED' ? 'Added' : DID[p.from];
  return [verb, when(p.doneAt), p.doneBy].filter(Boolean).join(' · ');
}

interface Move { from: string | null; to: string; note: string | null }

function move(p: UndoPreview): Move | null {
  if (p.kind === 'STOCK') return null;
  if (p.kind === 'DELIVERED') return { from: 'Delivered', to: 'Dispatched', note: null };
  if (p.kind === 'RELEASE') return { from: 'Production', to: 'Pre-Press', note: 'staged' };
  if (p.kind === 'ENTRY') {
    return { from: null, to: p.stageName, note: `${fmt(p.removedQuantity + p.keptQuantity)} → ${fmt(p.keptQuantity)} recorded` };
  }
  if (p.to === 'REMOVED') return null;
  const note = p.to === 'IN_PROGRESS'
    ? (p.keptQuantity > 0 ? `running · ${fmt(p.keptQuantity)} recorded` : 'running')
    : p.from === 'SKIPPED' ? 'back on the route' : 'not started';
  const from = p.currentStage && p.currentStage !== p.stageName ? p.currentStage : null;
  return { from, to: p.stageName, note };
}

function title(p: UndoPreview) {
  if (!p.allowed) return p.kind === 'STOCK' && p.heading?.startsWith('Reopen') ? "Can't reopen yet" : "Can't undo yet";
  if (p.kind === 'STOCK') return p.heading ?? 'Undo?';
  if (p.kind === 'RELEASE') return 'Undo Release?';
  if (p.kind === 'DELIVERED') return 'Undo Delivered?';
  if (p.kind === 'ENTRY') return `Undo ${fmt(p.removedQuantity)} recorded?`;
  if (p.to === 'REMOVED') return `Undo adding ${p.stageName}?`;
  const step = p.from === 'COMPLETED' ? 'Finish' : p.from === 'SKIPPED' ? 'Skip' : 'Start';
  return `Undo ${step} ${p.stageName}?`;
}

function extras(p: UndoPreview) {
  if (p.kind === 'STOCK') return p.changes ?? [];
  if (p.kind === 'DELIVERED') return [p.doneAt ? `Delivery date ${fmtDate(p.doneAt)} is cleared` : 'Delivery date is cleared'];
  if (p.kind === 'RELEASE') return p.boards.map((b) => `${fmt(b.sheets)} sheets of ${b.name} back to stock`);
  if (p.kind === 'ENTRY') return [];
  const lines: string[] = [];
  if (p.to === 'REMOVED') lines.push(`${p.stageName} comes off the route`);
  if (p.to === 'PENDING' && p.from === 'IN_PROGRESS' && p.removedQuantity > 0) {
    lines.push(`The ${fmt(p.removedQuantity)} recorded is removed`);
  }
  if (p.tookBack.length > 0) lines.push(`Also goes back: ${p.tookBack.map((t) => t.name).join(', ')}`);
  return lines;
}

function notice(p: UndoPreview) {
  if (p.kind === 'STOCK') {
    return p.heading?.startsWith('Reopen') || p.lastWhat === 'Split' || p.undoUrl || !p.lastWhat ? p.summary : `${p.lastWhat} undone · ${p.subtitle}`;
  }
  const j = p.jobNumber;
  if (p.kind === 'DELIVERED') return `${j} back to Dispatched`;
  if (p.kind === 'RELEASE') return `${j} back to Pre-Press`;
  if (p.kind === 'ENTRY') return `${j}: ${fmt(p.removedQuantity)} removed from ${p.stageName}`;
  if (p.to === 'REMOVED') return `${p.stageName} taken off ${j}`;
  return `${j} back to ${p.stageName}`;
}

export default function UndoDialog({ previewUrl, onClose, onDone }: {
  previewUrl: string;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [p, setP] = useState<UndoPreview | null>(null);
  const [reason, setReason] = useState('Misclick');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get<UndoPreview>(previewUrl)
      .then(setP)
      .catch((e) => setError(e instanceof Error ? e.message : 'Could not check what can be undone'));
  }, [previewUrl]);

  async function undo() {
    if (!p) return;
    setBusy(true); setError('');
    try {
      const url = p.kind === 'STOCK' ? p.undoUrl ?? previewUrl
        : p.kind === 'DELIVERED'
        ? `/api/dispatch/${p.dispatchId}/undo-delivered`
        : p.kind === 'RELEASE'
        ? `/api/production/jobs/${p.jobId}/undo-release`
        : p.kind === 'ENTRY'
        ? `/api/production/entries/${p.entryId}/undo`
        : `/api/production/job-stages/${p.jobStageId}/undo`;
      await api.post(url, { reason: reason.trim() || 'Misclick' });
      onDone(notice(p));
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not undo');
    } finally {
      setBusy(false);
    }
  }

  const steps = p ? (p.tookBack?.length ?? 0) + 1 : 1;

  return (
    <div className="backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 420 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{p ? title(p) : 'Undo'}</h3>
          {p && <p>{p.kind === 'STOCK' ? p.subtitle : [p.jobNumber, p.customer, p.box].filter(Boolean).join(' · ')}</p>}
        </div>
        <div className="modal-body">
          {error && <div className="error">{error}</div>}
          {!p && !error && <div className="dim">Checking…</div>}
          {p && !p.allowed && <div>{p.blocked}</div>}
          {p && p.allowed && (
            <>
              {done(p) && <div className="undo-when">{done(p)}</div>}
              {move(p) && (
                <div className="undo-move">
                  {move(p)!.from && <><strong>{move(p)!.from}</strong><span className="undo-arrow">→</span></>}
                  <strong>{move(p)!.to}</strong>
                  {move(p)!.note && <span className="dim"> · {move(p)!.note}</span>}
                </div>
              )}
              {p.counts && p.counts.length > 0 && (
                <div className="order-list undo-boards">
                  <table className="order-table compact">
                    <colgroup><col /><col style={{ width: 80 }} /><col style={{ width: 80 }} /></colgroup>
                    <thead><tr><th>Board</th><th className="num">On hand</th><th className="num">After</th></tr></thead>
                    <tbody>
                      {p.counts.map((b) => (
                        <tr key={b.name}>
                          <td>{b.name}</td>
                          <td className="num dim">{fmt(b.now)}</td>
                          <td className="num strong">{fmt(b.after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {extras(p).length > 0 && (
                <ul className="undo-list">
                  {extras(p).map((line) => <li key={line}>{line}</li>)}
                </ul>
              )}
              <div className="field" style={{ marginBottom: 0, marginTop: 18 }}>
                <label htmlFor="undo-reason">Reason</label>
                <input id="undo-reason" type="text" value={reason}
                       onFocus={(e) => e.target.select()}
                       onChange={(e) => setReason(e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div className="modal-foot">
          {p && !p.allowed ? (
            <button className="btn primary" onClick={onClose}>Close</button>
          ) : (
            <>
              <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
              <button className="btn primary" onClick={() => void undo()} disabled={busy || !p}>
                {p?.kind === 'STOCK' && p.heading?.startsWith('Reopen') ? (busy ? 'Reopening…' : 'Reopen')
                  : busy ? 'Undoing…' : steps > 1 ? `Undo ${steps} steps` : 'Undo'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
